// Contracts region layer — admin-region polygons (country → admin1 →
// admin2) tinted by per-region contract volume. Replaces the old
// `contracts-hex` HeatmapLayer (kernel-density blobs over province
// centroids) with crisp boundary polygons that read like Palantir /
// Silobreaker choropleths.
//
// Geometry + metrics arrive together in ONE response: `/v1/map/regions`
// (T9) returns a GeoJSON FeatureCollection where each feature carries the
// polygon geometry AND `properties.contract_count`. deck.gl `GeoJsonLayer`
// renders it directly — no tiles, no client-side join.
//
// Why GeoJsonLayer, not MVTLayer: the current dataset is ~400 admin
// polygons across 5 countries (2.3MB GeoJSON, bbox-clipped server-side to
// far less per viewport). At that scale a direct GeoJsonLayer is simpler,
// has zero heavy transitive deps (@deck.gl/geo-layers pulls
// @loaders.gl/arrow + apache-arrow, which broke the Vite build under the
// repo's strict pnpm layout), and renders identically. T8's MVT route +
// PMTiles archive stay in place for the future 10k+ region case; the
// frontend simply doesn't consume them yet.
//
// Color: SAME blue lerp ramp the old contracts-hex used —
// `[10,22,56] → [200,230,255]` with sqrt compression so a single
// high-volume region doesn't burn out the ramp. A region with NO metric
// (zero contracts) renders a neutral low-alpha gray — NOT transparent —
// per the states catalog (Surface 2 / success: "no data" must remain a
// visible, clickable polygon, not a hole).
//
// Stroke: per-basemap (D-AS2). Terrain = 1px cyan @ 60%, satellite = 1px
// white halo @ 80%, dark = current treatment. The active `basemapVariant`
// threads through `props`. Full per-variant fill-saturation adaptation is
// T21 — this layer ships the stroke split and leaves fill uniform.

import { GeoJsonLayer } from '@deck.gl/layers';
import type { LayerSpec, RegionFeature } from './registry.js';

// Blue lerp endpoints — identical to the values the contracts-hex ramp
// terminated at (`[10,22,56]` low → `[200,230,255]` high). The old layer
// used a 6-stop HeatmapLayer colorRange; a choropleth fill is a single
// continuous lerp between these two anchors, which is the ramp D-AS2 locked.
const LOW: [number, number, number] = [10, 22, 56];
const HIGH: [number, number, number] = [200, 230, 255];

// "No data" neutral — a desaturated slate at low alpha. Visible (so the
// polygon stays a click target and reads as "in the dataset, zero
// contracts") but clearly distinct from the blue value ramp. NOT [0,0,0,0]
// — transparent would make the region a hole, which the states catalog
// explicitly forbids.
const NO_DATA: [number, number, number, number] = [90, 100, 120, 40];

// Fill alpha for regions that DO have a metric. Kept below full opacity so
// the basemap labels + borders read through the choropleth.
const FILL_ALPHA = 170;

/** Linear interpolate each channel of two RGB triples by t∈[0,1]. */
function lerpRgb(t: number): [number, number, number] {
  const c = Math.max(0, Math.min(1, t));
  return [
    Math.round(LOW[0] + (HIGH[0] - LOW[0]) * c),
    Math.round(LOW[1] + (HIGH[1] - LOW[1]) * c),
    Math.round(LOW[2] + (HIGH[2] - LOW[2]) * c),
  ];
}

/**
 * Fill color for one region given its contract_count and the viewport max.
 * sqrt-compresses the long tail (mirrors contracts-hex `getWeight`), then
 * lerps the blue ramp. count ≤ 0 or missing → caller handles NO_DATA.
 */
export function regionFillColor(count: number, maxCount: number): [number, number, number, number] {
  if (count <= 0 || maxCount <= 0) return NO_DATA;
  // sqrt distribution — same long-tail compression contracts-hex applied
  // via `Math.sqrt(count)` on the heat weight. Normalize against the sqrt
  // of the viewport peak so the busiest region hits the bright end.
  const t = Math.sqrt(count) / Math.sqrt(maxCount);
  const [r, g, b] = lerpRgb(t);
  return [r, g, b, FILL_ALPHA];
}

/** Per-basemap stroke (D-AS2). Returns [r,g,b,a]. */
export function regionLineColor(
  variant: 'terrain' | 'sat' | 'dark',
): [number, number, number, number] {
  switch (variant) {
    case 'sat':
      // White halo @ 80% — reads over bright satellite imagery.
      return [255, 255, 255, 204];
    case 'dark':
      // Current treatment — brand cyan, low opacity, matches the old
      // contracts-hex / density stroke mood on the dark vector basemap.
      return [56, 189, 248, 110];
    default:
      // terrain — 1px cyan @ 60%.
      // T21-followup: per-variant fill-saturation adaptation also lands here.
      return [56, 189, 248, 153];
  }
}

export const contractsRegionLayer: LayerSpec = {
  id: 'contracts-region',
  label: 'CON · contracts',
  kind: 'contracts-region',
  signalTypes: [],
  color: 'var(--blue)',
  colorAxis: 'category',
  description: 'Contract volume by region — blue choropleth, deepens with zoom',
  classification: 'OPEN',
  legend: {
    swatch: 'var(--blue)',
    label: 'CON',
    hint: 'Per-region contract volume — admin-boundary choropleth (zoom to subdivide)',
  },
  // On by default — replaces contracts-hex in the default-on set. The
  // legend only surfaces ENABLED layers, so default-off would hide the
  // toggle entirely (the same chicken-and-egg the old layer documented).
  defaultEnabled: true,
  // No IntelSignal rows feed this layer; geometry + values both come from
  // `props.regionFeatures` (the /v1/map/regions FeatureCollection).
  select: () => [],
  visibleAtZoom: () => true,
  deckLayer: ({ regionFeatures, basemapVariant, onRegionClick }) => {
    const features = regionFeatures ?? [];
    // Empty data → render nothing (states catalog: don't paint an empty
    // layer; the SPA shows the "NO CONTRACTS IN VIEW" card instead).
    if (features.length === 0) return null;

    const variant = basemapVariant ?? 'terrain';
    // sqrt-normalization peak — the busiest region in the current payload.
    const maxCount = features.reduce((mx, ft) => Math.max(mx, ft.properties.contract_count), 0);

    return new GeoJsonLayer<RegionFeature['properties']>({
      id: 'intel-contracts-region',
      data: features as RegionFeature[],
      pickable: true,
      // Hover feedback so the region reads as interactive (paired with the
      // overlay getTooltip that surfaces name + contract count).
      autoHighlight: true,
      highlightColor: [56, 189, 248, 80],
      stroked: true,
      filled: true,
      lineWidthUnits: 'pixels',
      getLineWidth: 1,
      getLineColor: regionLineColor(variant),
      getFillColor: (f: RegionFeature) => {
        const count = f.properties?.contract_count ?? 0;
        if (count <= 0) return NO_DATA;
        return regionFillColor(count, maxCount);
      },
      // Recompute fill/stroke only when inputs change — keyed by feature
      // count + peak + variant. (deck.gl diffs `data` by reference; a new
      // viewport fetch yields a fresh array, re-rendering the polygons.)
      updateTriggers: {
        getFillColor: [features.length, maxCount],
        getLineColor: variant,
      },
      // Region drill-down: fire the consumer's callback with the clicked
      // feature's properties (region_id, name, contract_count). Returning
      // true stops the click from falling through to the always-on pick
      // overlay below, which would otherwise swallow the region click as
      // a "no signal selected" deselect.
      onClick: (info) => {
        if (!onRegionClick) return false;
        const obj = info.object as RegionFeature | null;
        if (!obj?.properties) return false;
        onRegionClick(obj.properties);
        return true;
      },
    });
  },
};
